import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';

import { terminal } from '../app/terminal.js';
import { useApp, useQuotes } from '@weke/ui';
import {
  formatMoney,
  formatPriceCompact,
  formatSignedPrice,
  priceDelta,
  priceDigits,
  spreadPoints,
} from '@weke/ui';
import type { Quote } from '@weke/ui';
import type { PreviewSymbol } from '@weke/protocol';

type SortKey = 'name' | 'bid' | 'ask';
type Tab = 'symbols' | 'details' | 'trading' | 'ticks';

interface Row {
  readonly symbol: PreviewSymbol;
  readonly quote: Quote | undefined;
}

/**
 * Market watch, in the desk idiom: a dense list, bid/ask on the tick grid, the
 * quote time, and a direction glyph per row (never colour alone — the arrow says
 * which way it moved even in greyscale).
 */
export function MarketWatch(): ReactElement {
  const app = useApp();
  const { quotes } = useQuotes();
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; ascending: boolean }>({
    key: 'name',
    ascending: true,
  });
  const [tab, setTab] = useState<Tab>('symbols');

  const rows = useMemo<Row[]>(() => {
    const list = app.symbols.map((symbol) => ({ symbol, quote: quotes[symbol.name] }));
    const needle = filter.trim().toUpperCase();
    const filtered =
      needle === '' ? list : list.filter((row) => row.symbol.name.toUpperCase().includes(needle));
    const direction = sort.ascending ? 1 : -1;
    return [...filtered].sort((left, right) => {
      if (sort.key === 'name') {
        return left.symbol.name < right.symbol.name ? -direction : direction;
      }
      const leftValue = sort.key === 'bid' ? left.quote?.bid : left.quote?.ask;
      const rightValue = sort.key === 'bid' ? right.quote?.bid : right.quote?.ask;
      if (leftValue === undefined || rightValue === undefined) {
        return leftValue === undefined ? 1 : -1;
      }
      const delta = BigInt(leftValue) - BigInt(rightValue);
      return delta < 0n ? -direction : delta > 0n ? direction : 0;
    });
  }, [app.symbols, quotes, filter, sort]);

  const selected = app.symbols.find((symbol) => symbol.name === app.selectedSymbol);

  return (
    <section className="panel min-h-0 flex-[3_1_0]" aria-label="Market watch">
      <header className="panel-head">
        <span>Market watch</span>
        <span className="faint">{app.symbols.length}</span>
        <input
          className="field ml-auto h-[17px] w-24 px-1 text-2xs"
          placeholder="Filter"
          aria-label="Filter symbols"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      </header>
      <div className="panel-body">
        {tab === 'symbols' ? (
          <table className="data-table compact" aria-label="Market watch">
            <thead>
              <tr>
                <Th
                  label="Symbol"
                  active={sort.key === 'name'}
                  ascending={sort.ascending}
                  onClick={() => setSort(flip(sort, 'name'))}
                />
                <Th
                  label="Bid"
                  align="right"
                  active={sort.key === 'bid'}
                  ascending={sort.ascending}
                  onClick={() => setSort(flip(sort, 'bid'))}
                />
                <Th
                  label="Ask"
                  align="right"
                  active={sort.key === 'ask'}
                  ascending={sort.ascending}
                  onClick={() => setSort(flip(sort, 'ask'))}
                />
                <Th label="Time" align="right" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const digits = priceDigits(row.symbol.digits, row.symbol.tick_size_e8);
                const open = app.dayOpen[row.symbol.name];
                const change =
                  row.quote === undefined || open === undefined
                    ? null
                    : priceDelta(open, row.quote.bid);
                return (
                  <tr
                    data-symbol={row.symbol.name}
                    aria-selected={app.selectedSymbol === row.symbol.name}
                    tabIndex={0}
                    onClick={() => terminal.selectSymbol(row.symbol.name)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') terminal.selectSymbol(row.symbol.name);
                    }}
                  >
                    <td className="font-medium">
                      {row.symbol.name}
                      <span className={`ml-1 text-2xs ${directionClass(change)}`} aria-hidden="true">
                        {directionArrow(change)}
                      </span>
                      <span className="sr-only">{directionLabel(change)}</span>
                    </td>
                    <td className="num text-right">
                      {row.quote === undefined ? '—' : formatPriceCompact(row.quote.bid, digits)}
                    </td>
                    <td className="num text-right">
                      {row.quote === undefined ? '—' : formatPriceCompact(row.quote.ask, digits)}
                    </td>
                    <td className="num text-right text-2xs text-muted">
                      {row.quote === undefined
                        ? '—'
                        : new Date(row.quote.receivedAtMs).toISOString().slice(11, 19)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <SymbolDetail tab={tab} symbol={selected} quote={selected === undefined ? undefined : quotes[selected.name]} />
        )}
      </div>
      <div className="tabs" role="tablist" aria-label="Market watch tabs">
        {(['symbols', 'details', 'trading', 'ticks'] as const).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            className="tab capitalize"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </div>
    </section>
  );
}

function Th(props: {
  readonly label: string;
  readonly align?: 'left' | 'right';
  readonly active?: boolean;
  readonly ascending?: boolean;
  readonly onClick?: () => void;
}): ReactElement {
  const marker = props.active === true ? (props.ascending === true ? '▴' : '▾') : '';
  return (
    <th
      className={props.align === 'right' ? 'text-right' : undefined}
      aria-sort={
        props.active === true ? (props.ascending === true ? 'ascending' : 'descending') : 'none'
      }
    >
      {props.onClick === undefined ? (
        <span>{props.label}</span>
      ) : (
        <button type="button" className="hover:text-text" onClick={props.onClick}>
          {props.label} <span className="faint">{marker}</span>
        </button>
      )}
    </th>
  );
}

function flip(
  sort: { key: SortKey; ascending: boolean },
  key: SortKey,
): { key: SortKey; ascending: boolean } {
  return sort.key === key ? { key, ascending: !sort.ascending } : { key, ascending: true };
}

function directionArrow(change: string | null): string {
  if (change === null) return '';
  if (change.startsWith('-')) return '↓';
  return /^0+$/.test(change) ? '' : '↑';
}

function directionClass(change: string | null): string {
  if (change === null) return 'faint';
  if (change.startsWith('-')) return 'sell';
  return /^0+$/.test(change) ? 'faint' : 'buy';
}

function directionLabel(change: string | null): string {
  if (change === null) return 'no reference';
  if (change.startsWith('-')) return 'below the session open';
  return /^0+$/.test(change) ? 'unchanged' : 'above the session open';
}

/** The non-table tabs: symbol facts, execution rules, and the recent tick tape. */
function SymbolDetail(props: {
  readonly tab: Tab;
  readonly symbol: PreviewSymbol | undefined;
  readonly quote: Quote | undefined;
}): ReactElement {
  const { symbol, quote } = props;
  const app = useApp();
  const tape = useTickTape(props.tab === 'ticks' ? symbol?.name : undefined, quote);

  if (symbol === undefined) {
    return <p className="p-2 text-muted">Select a symbol.</p>;
  }
  const digits = priceDigits(symbol.digits, symbol.tick_size_e8);

  if (props.tab === 'ticks') {
    return (
      <table className="data-table compact" aria-label="Tick tape">
        <thead>
          <tr>
            <th>Time</th>
            <th className="text-right">Bid</th>
            <th className="text-right">Ask</th>
          </tr>
        </thead>
        <tbody data-testid="tick-tape">
          {tape.map((tick, index) => (
            <tr key={`${tick.receivedAtMs}-${index}`}>
              <td className="num text-2xs text-muted">
                {new Date(tick.receivedAtMs).toISOString().slice(11, 23)}
              </td>
              <td className="num text-right">{formatPriceCompact(tick.bid, digits)}</td>
              <td className="num text-right">{formatPriceCompact(tick.ask, digits)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  const fields: [string, string][] =
    props.tab === 'trading'
      ? [
          ['Execution', symbol.exec_mode],
          ['Fill policy', symbol.fill_policy],
          ['Order types', symbol.allowed_order_types.join(', ')],
          ['Volume min', formatVolumeE4(symbol.volume_min_e4)],
          ['Volume max', formatVolumeE4(symbol.volume_max_e4)],
          ['Volume step', formatVolumeE4(symbol.volume_step_e4)],
          ['Stop level', formatPriceCompact(symbol.stop_level_e8, digits)],
          ['Freeze level', formatPriceCompact(symbol.freeze_level_e8, digits)],
        ]
      : [
          ['Symbol', symbol.name],
          ['Base / quote', `${symbol.base_ccy} / ${symbol.quote_ccy}`],
          ['Digits', String(digits)],
          ['Tick size', formatPriceCompact(symbol.tick_size_e8, 8)],
          ['Contract size', symbol.contract_size],
          [
            'Spread',
            quote === undefined
              ? '—'
              : `${spreadPoints(quote.bid, quote.ask, symbol.tick_size_e8)} pt`,
          ],
          [
            'Session open',
            app.dayOpen[symbol.name] === undefined
              ? '—'
              : formatPriceCompact(app.dayOpen[symbol.name], digits),
          ],
          [
            'Change',
            app.dayOpen[symbol.name] === undefined || quote === undefined
              ? '—'
              : formatSignedPrice(priceDelta(app.dayOpen[symbol.name] ?? quote.bid, quote.bid), digits, true),
          ],
          // Margin per lot is the server's business; the ticket shows the
          // *account* requirement for the size actually chosen.
          [
            'Account',
            app.account === null ? '—' : formatMoney(app.account.balance_minor, app.account.digits),
          ],
        ];

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 p-2 text-2xs">
      {fields.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted">{label}</dt>
          <dd className="num text-right text-text">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatVolumeE4(value: string): string {
  const text = value.padStart(5, '0');
  return `${text.slice(0, -4)}.${text.slice(-4)}`;
}

/**
 * A bounded tick tape for the selected symbol.
 *
 * It is fed from the same batched quote board the table uses, so it shows what
 * the socket delivered, in order — not a reconstructed history.
 */
function useTickTape(symbol: string | undefined, quote: Quote | undefined): Quote[] {
  const [tape, setTape] = useState<Quote[]>([]);
  const lastRef = useRef(0);
  useEffect(() => {
    if (symbol === undefined || quote === undefined) return;
    if (quote.receivedAtMs === lastRef.current) return;
    setTape((current) => [quote, ...current].slice(0, 14));
  }, [symbol, quote]);
  useEffect(() => {
    setTape([]);
    lastRef.current = 0;
  }, [symbol]);
  return tape;
}
